import { normalize } from 'normalizr';
import { ofType } from 'redux-observable';
import { of } from 'rxjs';
import { mergeMap, exhaustMap, switchMap, catchError } from 'rxjs/operators';

import { analyticsUpdatedPII } from '@perpay-web/fintech/actions/analytics/userInfo';
import {
    postLogout,
    refreshAccessToken,
} from '@perpay-web/fintech/actions/authentication';
import {
    fetchUserInfoForDataModule as fetchUserInfoForDataModuleAction,
    fetchUserInfoSuccess,
    fetchUserInfoError,
    updateUserInfoForDataModule as updateUserInfoForDataModuleAction,
    updateUserInfoSuccess,
    updateUserInfoErrorForDataModule,
    replaceUserInfo,
    updateUserInfoError,
    deactivateAccountError,
    analyticsProfileInformationUpdated,
    updateUserInfoContextSuccess as updateUserInfoContextSuccessAction,
    updateUserInfoForCardApplication,
    updateUserInfoForCardApplicationSuccess,
    updateUserInfoForCardApplicationError,
} from '@perpay-web/fintech/actions/entities/userInfo';
import { routeToLocation } from '@perpay-web/fintech/actions/router';
import { cardUpdateUserInfoError } from '@perpay-web/fintech/actions/ui/cardEditAddress';
import { APPLICATION_STEP } from '@perpay-web/fintech/components/screens/CardApplication/CardApplicationSteps';
import { cardApplicationSetSteps } from '@perpay-web/fintech/components/screens/CardApplication/useCardApplicationContext';
import {
    FETCH_USER_INFO,
    UPDATE_USER_INFO,
    BACKEND_CARD_UPDATE_USER_INFO,
    BACKEND_DEACTIVATE_ACCOUNT,
} from '@perpay-web/fintech/constants/actionTypes';
import {
    USERS_ENDPOINT,
    USER_DEACTIVATION_ENDPOINT,
} from '@perpay-web/fintech/constants/urls';
import { cardApplicationConfirmAddressModalOpen } from '@perpay-web/fintech/hooks/useCardApplicationConfirmAddressModalContext';
import { userInfo } from '@perpay-web/fintech/normalizers/schemas';
import { paths } from '@perpay-web/fintech/props/appPaths';
import { getUserInfo } from '@perpay-web/fintech/selectors/entities/userInfo';
import { checkoutUpdateUserInformationSuccess } from '@perpay-web/hooks/actions/checkoutUpdateUserInformation';
import { handleErrorMessageWithFallback } from '@perpay-web/observable/operators/handleErrorMessageWithFallback';
import {
    getAnalyticsIdentity,
    retrieveToken,
} from '@perpay-web/utils/tokenUtils';

export const fetchUserInfoForDataModule = (action$, state$, { get }) =>
    action$.pipe(
        ofType(fetchUserInfoForDataModuleAction().type),
        switchMap(() => get(`${USERS_ENDPOINT}me/`)),
        mergeMap((results) => [fetchUserInfoSuccess(results.response)]),
        handleErrorMessageWithFallback((error) => [fetchUserInfoError(error)]),
    );

export const fetchUserInfo = (action$, state$, { get }) =>
    action$.pipe(
        ofType(FETCH_USER_INFO),
        switchMap(() => get(`${USERS_ENDPOINT}me/`)),
        mergeMap((results) => {
            const normalized = normalize(results.response, userInfo);
            return [replaceUserInfo(normalized.entities.userInfo)];
        }),
    );

export const updateUserInfoForDataModule = (action$, state$, { patch }) =>
    action$.pipe(
        ofType(updateUserInfoForDataModuleAction().type),
        mergeMap((action) => {
            const nonNullFields = Object.keys(action.payload)
                .filter((field) => action.payload[field] !== null)
                .reduce((output, field) => {
                    /* eslint-disable-next-line no-param-reassign */
                    output[field] = action.payload[field];
                    return output;
                }, {});
            const json = JSON.stringify(nonNullFields);
            return patch(`${USERS_ENDPOINT}me/`, json);
        }),
        mergeMap((results) => {
            const decodedTokens = retrieveToken();
            const identityAction = getAnalyticsIdentity(
                decodedTokens,
                results.response,
            );
            return [
                updateUserInfoSuccess(results.response),
                analyticsUpdatedPII(identityAction),
                analyticsProfileInformationUpdated(identityAction),
            ];
        }),
        handleErrorMessageWithFallback((error) => [
            updateUserInfoErrorForDataModule(error),
        ]),
    );

export function updateUserInfo(action$, state$, { patch }) {
    return action$.pipe(
        ofType(UPDATE_USER_INFO),
        mergeMap((action) => {
            const nonNullFields = Object.keys(action.payload)
                .filter((field) => action.payload[field] !== null)
                .reduce((output, field) => {
                    /* eslint-disable-next-line no-param-reassign */
                    output[field] = action.payload[field];
                    return output;
                }, {});
            const json = JSON.stringify(nonNullFields);
            return patch(`${USERS_ENDPOINT}me/`, json);
        }),
        mergeMap((results) => {
            const normalized = normalize(results.response, userInfo);

            const decodedTokens = retrieveToken();
            const identityAction = getAnalyticsIdentity(
                decodedTokens,
                getUserInfo(state$.value),
            );
            return [
                replaceUserInfo(normalized.entities.userInfo),
                routeToLocation(paths.profile.path),
                analyticsUpdatedPII(identityAction),
                analyticsProfileInformationUpdated(identityAction),
            ];
        }),
        handleErrorMessageWithFallback((error) => [updateUserInfoError(error)]),
    );
}

export function cardUpdateUserInfo(action$, state$, { patch }) {
    return action$.pipe(
        ofType(BACKEND_CARD_UPDATE_USER_INFO),
        mergeMap((action) =>
            patch(`${USERS_ENDPOINT}me/`, JSON.stringify(action.payload)),
        ),
        mergeMap((results) => {
            const normalized = normalize(results.response, userInfo);
            const decodedTokens = retrieveToken();
            const identityAction = getAnalyticsIdentity(
                decodedTokens,
                getUserInfo(state$.value),
            );
            return [
                replaceUserInfo(normalized.entities.userInfo),
                analyticsUpdatedPII(identityAction),
            ];
        }),
        handleErrorMessageWithFallback((error) => [
            cardUpdateUserInfoError(error),
        ]),
    );
}

/**
 * Epic to handle user info updates for card applications.
 *
 * This epic processes the updateUserInfoForCardApplication action by:
 * 1. Making a PATCH request to /users/me/ endpoint
 * 2. On success: normalizes data, refreshes tokens, and dispatches success actions
 * 3. On error: checks for specific address validation errors and shows modal if needed
 *
 * Key features:
 * - Filters out null fields before sending request
 * - Handles "Invalid address, more information is needed" error specifically
 * - Bypasses data module error corruption by catching HTTP errors directly
 * - Dispatches to dedicated card application data module for clean error state
 *
 * @param {Observable} action$ - Stream of Redux actions
 * @param {Observable} state$ - Stream of Redux state
 * @param {Object} dependencies - Epic dependencies
 * @param {Function} dependencies.patch - HTTP PATCH function
 * @returns {Observable} Stream of result actions
 */
export function cardApplicationUpdateUserInfo(action$, state$, { patch }) {
    return action$.pipe(
        ofType(updateUserInfoForCardApplication().type),
        exhaustMap((action) => {
            const nonNullFields = Object.keys(action.payload)
                .filter((field) => action.payload[field] !== null)
                .reduce((output, field) => {
                    /* eslint-disable-next-line no-param-reassign */
                    output[field] = action.payload[field];
                    return output;
                }, {});
            const json = JSON.stringify(nonNullFields);

            return patch(`${USERS_ENDPOINT}me/`, json).pipe(
                mergeMap((results) => {
                    const normalized = normalize(results.response, userInfo);
                    const decodedTokens = retrieveToken();
                    const identityAction = getAnalyticsIdentity(
                        decodedTokens,
                        getUserInfo(state$.value),
                    );
                    return [
                        replaceUserInfo(normalized.entities.userInfo),
                        refreshAccessToken(),
                        updateUserInfoForCardApplicationSuccess(
                            results.response,
                        ),
                        analyticsUpdatedPII(identityAction),
                    ];
                }),
                catchError((error) => {
                    const returnActions = [];

                    // Handle specific address validation error by showing confirmation modal
                    // This mirrors the PHO implementation and must be done before dispatching
                    // to data module to preserve the original error message structure
                    if (error.response && error.response.message) {
                        const { message } = error.response;

                        if (
                            Array.isArray(message) &&
                            message[0] ===
                                'Invalid address, more information is needed'
                        ) {
                            returnActions.push(
                                cardApplicationConfirmAddressModalOpen(),
                            );
                        }
                    }

                    // Always dispatch error to dedicated card application data module
                    // This ensures proper loading state management and error display
                    returnActions.push(
                        updateUserInfoForCardApplicationError(error),
                    );

                    return of(...returnActions);
                }),
            );
        }),
    );
}

export function cardApplicationUpdateUserInfoSuccess(action$) {
    return action$.pipe(
        ofType(updateUserInfoForCardApplicationSuccess().type),
        mergeMap(() => [cardApplicationSetSteps(APPLICATION_STEP)]),
    );
}

export function updateUserInfoContextSuccess(action$, state$) {
    return action$.pipe(
        ofType(
            updateUserInfoContextSuccessAction().type,
            checkoutUpdateUserInformationSuccess().type,
        ),
        mergeMap((results) => {
            const normalized = normalize(results.payload, userInfo);
            const decodedTokens = retrieveToken();
            const identityAction = getAnalyticsIdentity(
                decodedTokens,
                getUserInfo(state$.value),
            );
            return [
                replaceUserInfo(normalized.entities.userInfo),
                analyticsUpdatedPII(identityAction),
            ];
        }),
    );
}

export function deactivateAccount(action$, state$, { post }) {
    return action$.pipe(
        ofType(BACKEND_DEACTIVATE_ACCOUNT),
        exhaustMap((action) =>
            post(
                `${USER_DEACTIVATION_ENDPOINT}`,
                JSON.stringify(action.payload),
            ),
        ),
        mergeMap(() => [postLogout()]),
        handleErrorMessageWithFallback((error) => [
            deactivateAccountError(error),
        ]),
    );
}
